import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { cn } from '../../lib/utils';

const WIDTH = 300;
const GUTTER = 12;

/**
 * An ⓘ that explains a term in place. Opens on hover or keyboard focus, and
 * stays open on click so it can be read on a touch screen. The panel renders in
 * a portal because the places that need explaining — table headers, cards —
 * sit inside `overflow-auto` containers that would clip it.
 */
export function InfoTip({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const open = hovered || pinned;

  useEffect(() => {
    if (!open || !button.current) return;
    const rect = button.current.getBoundingClientRect();
    const left = Math.min(Math.max(GUTTER, rect.left - WIDTH / 2), window.innerWidth - WIDTH - GUTTER);
    setPosition({ top: rect.bottom + 6, left });
  }, [open]);

  useEffect(() => {
    if (!pinned) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent && e.key !== 'Escape') return;
      if (e instanceof MouseEvent && button.current?.contains(e.target as Node)) return;
      setPinned(false);
    };
    window.addEventListener('keydown', close);
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('keydown', close);
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [pinned]);

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          setPinned((p) => !p);
        }}
        className={cn(
          'inline-flex shrink-0 items-center text-muted-foreground transition-colors hover:text-foreground',
          pinned && 'text-accent',
          className,
        )}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open &&
        position &&
        createPortal(
          <div
            id={id}
            role="tooltip"
            style={{ top: position.top, left: position.left, width: WIDTH }}
            className="fixed z-[60] rounded-md border border-line bg-panel px-3 py-2.5 text-left text-xs leading-relaxed font-normal text-fg-2 normal-case shadow-lg"
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
